/**
 * IMAI motion primitives for Motion (framer-motion) and WAAPI callers.
 *
 * Values come from the v5 landing showcases and match the CSS variables and
 * utilities in src/styles/imai-motion.css (`--imai-ease`, `imai-press`,
 * `imai-enter`, `imai-swap-in`). Keep the two in step.
 *
 * Surface note: button groups get an equal inset on all sides = the bottom
 * inset (see the notes in imai-motion.css and
 * src/components/ui/compact-dialog.ts).
 */

/** v5 `--ease`: a fast start that settles softly. */
export const IMAI_EASE = [0.22, 1, 0.36, 1] as const;

/** {@link IMAI_EASE} as a CSS timing function, for WAAPI `easing`. */
export const IMAI_EASE_CSS = 'cubic-bezier(0.22, 1, 0.36, 1)';

export const IMAI_DURATION_MS = {
  press: 150,
  hover: 150,
  exit: 160,
  swap: 260,
  enter: 420,
  /** Composer affordance reveal. */
  reveal: 500,
} as const;

export type ImaiDuration = keyof typeof IMAI_DURATION_MS;

/** Delay between items of a staggered entrance. */
export const IMAI_STAGGER_MS = 50;

/** Items past this index enter together, so long lists settle quickly. */
export const IMAI_STAGGER_MAX_INDEX = 10;

/** The entrance delay for the item at `index` (matches `.imai-enter`). */
export function imaiStaggerDelayMs(index: number): number {
  const step = Math.min(Math.max(0, Math.floor(index)), IMAI_STAGGER_MAX_INDEX);
  return step * IMAI_STAGGER_MS;
}

/** A Motion transition on the IMAI curve; instant under reduced motion. */
export function imaiTransition(
  duration: ImaiDuration,
  reduceMotion = false
): { duration: number; ease?: typeof IMAI_EASE } {
  if (reduceMotion) return { duration: 0 };
  return { duration: IMAI_DURATION_MS[duration] / 1000, ease: IMAI_EASE };
}
